/* eslint-disable react/jsx-pascal-case */
import { FontIcon, mergeStyles } from '@fluentui/react';
import React from 'react';
import { BwjLazyImage, ILazyImageStyles } from '../bwj-lazy-image';
import { Internal__ImageBrowser_ScrollPaddle } from './scroll-paddle';
import { internal__getImageBrowserPartStyles, internal__getImageBrowserRootStyle } from './styles';
import $ from 'jquery';

export interface IBrowserImage {
  src: string;
  alt?: string | undefined;
}

export interface IBwjImageBrowserContextButtonProps {
  icon: string;
  onClick: (image: IBrowserImage, id?: string) => void;
}

interface IBwjImageBrowserProps {
  id?: string;
  images: IBrowserImage[];
  displayIndex: number;
  loadImages: boolean | null | undefined;
  imageWidth: number;
  widthToHeightRatio?: number | undefined;
  contextButton?: IBwjImageBrowserContextButtonProps;
  onImagesLiked?: (id?: string) => void;
  onDisplayIndexChanged?: (index: number, image: IBrowserImage) => void;
}

const DEFAULT_WIDTH_TO_HEIGHT_RATIO = 1.25;

const getImageStyles = (imageWidth: number) => {
  const iconSize = Math.floor(imageWidth / 5);
  const s: ILazyImageStyles = {
    root: { width: imageWidth },
    untouchedFrame: { icon: { fontSize: iconSize } },
    errorFrame: { icon: { fontSize: iconSize } },
    loadingFrame: { icon: { fontSize: iconSize } },
  };
  return s;
}

export const BwjImageBrowser: React.FunctionComponent<IBwjImageBrowserProps> = (props) => {
  const aspectRatio = props.widthToHeightRatio ?? DEFAULT_WIDTH_TO_HEIGHT_RATIO;
  const imageHeight = props.imageWidth * aspectRatio;
  const partClasses = internal__getImageBrowserPartStyles(4000);
  const rootStyle = internal__getImageBrowserRootStyle(props.imageWidth, imageHeight);
  const individualImageStyle = getImageStyles(props.imageWidth);
  const rootClassName = mergeStyles(rootStyle);
  const cartridgeWidth = props.imageWidth * props.images.length;
  const cartridgePos = props.imageWidth * props.displayIndex * -1;
  const cartridgeId = `imgBrowser-${props.id}-cartridge`;
  const navId = `imgBrowser-${props.id}-nav`;
  const likeIndicatorId = `imgBrowser-${props.id}-liked`;

  const onShowNext = () => {
    const $cartridge = $(`#${cartridgeId}`);
    const $nav = $(`#${navId}`);
    $nav.hide(100);
    $cartridge.animate({
      left: `-=${props.imageWidth}`,
    }, 250, () => {
      $nav.show(100);
      const idx = props.displayIndex + 1;
      !!props.onDisplayIndexChanged && props.onDisplayIndexChanged(idx, props.images[idx]);
    });
  };

  const onShowPrevious = () => {
    const $cartridge = $(`#${cartridgeId}`);
    const $nav = $(`#${navId}`);
    $nav.hide(100);
    $cartridge.animate({
      left: `+=${props.imageWidth}`,
    }, 250, () => {
      $nav.show(100);
      const idx = props.displayIndex - 1;
      !!props.onDisplayIndexChanged && props.onDisplayIndexChanged(idx, props.images[idx]);
    });
  };

  const onGlassDoubleClick = () => {
    const $likeIndicator = $(`#${likeIndicatorId}`);
    const $icon = $($likeIndicator.children()[0]);

    $likeIndicator.addClass(partClasses.showLikeIndicator);
    $icon.animate({
      opacity: '0',
      fontSize: '75px',
    }, 1000, () => {
      $likeIndicator.removeClass(partClasses.showLikeIndicator);
      $icon.removeAttr('style');
    });

    !!props.onImagesLiked && props.onImagesLiked(props.id);
  };

  let rapidClickCount = 0;
  const onGlassClick = () => {
    // this event only matters if we are supporting image liking
    if(!props.onImagesLiked) { return; }

    rapidClickCount++;
    if(rapidClickCount === 1) {
      // this will timeout the period during which we observe subsequent clicks on the glass,
      // and interpret them as all being a part of the same gesture
      window.setTimeout(() => {
        rapidClickCount = 0;
      }, 350);
    }
    else if(rapidClickCount === 2) {
      onGlassDoubleClick();
    }
  };

  return (
    <div className={rootClassName}>
        <div id={cartridgeId} className={partClasses.cartridge} style={{width: cartridgeWidth, left: cartridgePos}}>
            {props.images.map(img => <BwjLazyImage
              key={img.src}
              src={img.src}
              alt={img.alt}
              loadImage={props.loadImages}
              styles={individualImageStyle}
            />)}
        </div>
        <div className={partClasses.glassCover} role='button' onClick={e => onGlassClick()}>
            <div id={navId} className={partClasses.nav}>
                {props.displayIndex > 0 &&
                <Internal__ImageBrowser_ScrollPaddle icon='ChevronLeft' onClick={onShowPrevious}/>}
                <div className={partClasses.navSpacer} role='button' onClick={e => onGlassClick()}></div>
                {(props.displayIndex + 1) !== props.images.length &&
                <Internal__ImageBrowser_ScrollPaddle icon='ChevronRight' onClick={onShowNext}/>}
            </div>
        </div>
        {!!props.contextButton &&
        <div className={partClasses.contextButton} role='button' onClick={e => props.contextButton?.onClick(props.images[props.displayIndex], props.id)}>
          <FontIcon iconName={props.contextButton?.icon} />
        </div>}
        {!!props.onImagesLiked &&
        <div id={likeIndicatorId} className={partClasses.likeIndicator}>
          <FontIcon iconName='HeartFill' className={partClasses.likeIndicatorIcon} />
        </div>}
        <div className={partClasses.indexIndicatorContainer}>
          <div className={partClasses.indexIndicatorPanel}>
            {props.images.length > 1 && props.images.map((img, idx) => <div
              key={img.src}
              className={props.displayIndex === idx ? partClasses.indexIndicatorActive : partClasses.indexIndicatorPassive}
            />)}
          </div>
        </div>
    </div>
  );
};
